// src/crm/ugc/VideosTab.tsx
//
// Onglet « Vidéos » du programme UGC (spec § 6.2, maquette 8) : à gauche la file des idées et
// brouillons à relire (la plus ancienne d'abord, temps restant sur les 48 h d'une idée, « En retard »
// une fois le délai d'une idée ou d'un brouillon dépassé) ou
// « Toutes les vidéos » filtrées par personne et par état, avec les messages non lus de chaque
// vidéo ; à droite la vidéo : l'idée en blocs étiquetés comme la personne l'a remplie (titre,
// « Avant l'app » et « Avec l'app », ou à l'ancien format « Avant… » et « Depuis que j'utilise
// l'app Basalt Lab… », première phrase pour les idées qui en ont une ; un texte libre en un bloc
// « Idée »), chaque version avec son lecteur (lecture par plages)
// et son téléchargement, le fil de la vidéo et la zone de réponse (VideoThread), puis le
// commentaire, « Ce qui marche », « À retoucher » (une ligne par point) et les trois décisions.
// « Refuser » (sans retour) passe par une confirmation dans la page ; « Valider » et « Demander une
// correction » partent directement. `?video=<id>` (lien des alertes de message) ouvre cette vidéo
// dans « Toutes les vidéos ». Seule une vidéo choisie (clic, même sur celle déjà affichée, ou lien
// d'une alerte) a son fil marqué lu : celle que l'onglet ouvre d'office garde ses non-lus.

import { useEffect, useId, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { isPastDeadline } from '../../../functions/lib/ugc/replyDeadline'
import { ApiClientError } from '../../lib/api'
import { ideaParts } from '../../lib/ideaText'
import type { AdminVideoQueueItem, ReviewDecision, ReviewStage, UgcDraft, UgcVideo, VideoStatus } from '../../lib/ugcTypes'
import { joinedVersionOf, latestReadyOf } from '../../../functions/lib/ugc/versions'
import { previewSrc, restartOnFirstPlay } from '../../lib/videoPreview'
import { formatAgo, formatDateTime, formatDeadline, formatSize, VIDEO_STATUS_LABELS as STATUS_LABELS } from './format'
import {
  ugcKeys,
  useReviewVideo,
  useUgcMembers,
  useUgcVideo,
  useUgcVideos,
  type UgcVideosFilters,
  type UgcVideosView,
} from './hooks'
import { VideoThread } from './VideoThread'

const STATUSES = Object.keys(STATUS_LABELS) as VideoStatus[]

/** Étape qui attend une décision, ou `null` si la vidéo n'attend rien de l'admin. */
function pendingStage(status: VideoStatus): ReviewStage | null {
  if (status === 'idea_submitted') return 'idea'
  if (status === 'draft_submitted') return 'draft'
  return null
}

function draftUrl(draftId: string): string {
  return `/api/ugc/drafts/${encodeURIComponent(draftId)}/file`
}

/** Heure courante, relue chaque minute (ancienneté et temps restant). */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

/** Version dont la file montre un petit aperçu : la dernière prête, jamais une version retirée. */
function thumbOf(item: AdminVideoQueueItem): string | null {
  return item.draft && item.draft.status === 'ready' && item.draft.withdrawnAt === null ? item.draft.id : null
}

function itemType(item: AdminVideoQueueItem, view: UgcVideosView): string {
  if (view === 'all') return STATUS_LABELS[item.video.status]
  // Version envoyée par la personne avant la validation de son idée : relue avec l'idée.
  if (item.stage === 'idea') return item.draft?.status === 'ready' ? `Idée, version ${item.draft.version} jointe` : 'Idée'
  return item.draft && item.draft.version > 1 ? `Brouillon, version ${item.draft.version}` : 'Brouillon'
}

/** « modifiée après l'envoi, le 12 nov. 2026, 11 h 30 » : idée changée par la personne avant relecture. */
function editedLabel(video: UgcVideo): string | null {
  return video.ideaEditedAt ? `modifiée après l'envoi, le ${formatDateTime(video.ideaEditedAt)}` : null
}

function itemWhen(item: AdminVideoQueueItem, now: Date): string {
  const ago = formatAgo(item.submittedAt, now)
  return item.ideaDeadline ? `${ago} · ${formatDeadline(item.ideaDeadline, now)}` : ago
}

/** Idée ou brouillon dont l'échéance de réponse (replyDeadline.ts) est passée. */
function isLate(item: AdminVideoQueueItem, now: Date): boolean {
  const deadline = item.ideaDeadline ?? item.draftDeadline
  return deadline !== null && isPastDeadline(deadline, now)
}

/** « 1 nouveau message », « 3 nouveaux messages ». */
function unreadLabel(count: number): string {
  return count > 1 ? `${count} nouveaux messages` : '1 nouveau message'
}

/** Points d'une relecture : une ligne par point, lignes vides retirées. */
function splitPoints(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

/** `next` : état rendu par la décision (une idée validée avec une version jointe passe en relecture). */
function decidedText(stage: ReviewStage, decision: ReviewDecision, title: string, next: VideoStatus): string {
  if (decision === 'approved' && stage === 'idea' && next === 'draft_submitted') {
    return `Idée validée pour « ${title} » : la version jointe passe en relecture.`
  }
  if (decision === 'approved') return `${stage === 'idea' ? 'Idée validée' : 'Brouillon validé'} pour « ${title} ».`
  if (decision === 'changes_requested') return `Correction demandée pour « ${title} ».`
  return `Vidéo refusée : « ${title} ».`
}

/** Message de chaque erreur de la route de décision ; `reread` = la file et la vidéo sont relues. */
function reviewErrorText(error: Error): { text: string; reread: boolean } {
  if (!(error instanceof ApiClientError)) {
    return {
      text: "La décision n'a pas pu partir (réseau indisponible). Réessaie : si elle était déjà partie, la page te le dira.",
      reread: false,
    }
  }
  if (error.status === 409 && (error.code === 'idea_changed' || error.code === 'draft_changed')) {
    return { text: `${error.message} Elle vient d'être relue.`, reread: true }
  }
  if (error.status === 409) {
    return {
      text: "Cette vidéo a changé d'état entre-temps (décision prise ailleurs) : la file et la vidéo viennent d'être relues. Vérifie son état avant de décider.",
      reread: true,
    }
  }
  if (error.status === 404) {
    return { text: "Cette vidéo n'existe plus : la personne l'a peut-être supprimée. La file vient d'être relue.", reread: true }
  }
  if (error.code === 'network_error') {
    return { text: `La décision n'a pas été enregistrée (erreur ${error.status}). Réessaie dans un instant.`, reread: false }
  }
  return { text: error.message, reread: false }
}

function detailErrorText(error: Error): string {
  if (error instanceof ApiClientError && error.status === 404) {
    return "Cette vidéo n'existe plus : la personne l'a peut-être supprimée."
  }
  return `La vidéo n'a pas pu être chargée : ${error.message}`
}

// ── Versions ─────────────────────────────────────────────────────────────────

/** Aperçu d'une version : l'image de la demi-seconde (src/lib/videoPreview.ts). */
function previewUrl(draftId: string): string {
  return previewSrc(draftUrl(draftId))
}

/**
 * Version que la fiche montre à relire, calculée comme le serveur (functions/lib/ugc/versions.ts) :
 * jointe à l'idée (retirées ignorées) ou dernière prête pour un brouillon ; `null` sans version.
 */
function shownVersion(stage: ReviewStage, drafts: readonly UgcDraft[]): number | null {
  const states = drafts.map((d) => ({ status: d.status, version: d.version, withdrawn: d.withdrawnAt !== null }))
  return (stage === 'idea' ? joinedVersionOf(states) : latestReadyOf(states))?.version ?? null
}

function DraftVersion({ draft, now }: { draft: UgcDraft; now: Date }) {
  const [failed, setFailed] = useState(false)
  if (draft.status !== 'ready') {
    const why = draft.withdrawnAt ? 'retirée par la personne' : 'fichier effacé'
    return (
      <figure className="ugc-version">
        <figcaption className="ugc-muted">{`Version ${draft.version} · ${draft.filename} · ${why}`}</figcaption>
      </figure>
    )
  }
  const url = draftUrl(draft.id)
  return (
    <figure className="ugc-version">
      <video
        className="ugc-player"
        controls
        playsInline
        preload="metadata"
        src={previewUrl(draft.id)}
        aria-label={`Version ${draft.version}`}
        onError={() => setFailed(true)}
        onPlay={(e) => restartOnFirstPlay(e.currentTarget)}
      />
      {failed && (
        <p className="ugc-error">
          Lecture impossible ici (format non lu par ce navigateur, ou fichier effacé entre-temps). Essaie « Télécharger ».
        </p>
      )}
      <figcaption className="ugc-version-meta">
        <span className="ugc-muted">
          {`Version ${draft.version} · ${draft.filename} · ${formatSize(draft.size)} · envoyée ${formatAgo(draft.completedAt ?? draft.createdAt, now)}`}
        </span>
        <a className="ugc-link" href={url} download aria-label={`Télécharger la version ${draft.version}`}>
          Télécharger
        </a>
      </figcaption>
    </figure>
  )
}

// ── Décision ─────────────────────────────────────────────────────────────────

/** Texte des trois champs d'une relecture : commentaire, « Ce qui marche », « À retoucher ». */
type ReviewDraft = { comment: string; highlights: string; fixes: string }

const EMPTY_REVIEW: ReviewDraft = { comment: '', highlights: '', fixes: '' }

function DecisionForm({
  draft,
  onDraft,
  busy,
  onDecide,
}: {
  draft: ReviewDraft
  onDraft: (value: ReviewDraft) => void
  busy: boolean
  onDecide: (decision: ReviewDecision) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const commentId = useId()
  const helpId = useId()
  const highlightsId = useId()
  const fixesId = useId()
  const empty = draft.comment.trim() === ''

  return (
    <div className="ugc-inline-form">
      <h3 className="ugc-subtitle">Décision</h3>
      <label htmlFor={commentId}>Ton commentaire (obligatoire pour une correction)</label>
      <textarea
        id={commentId}
        className="ugc-textarea"
        rows={3}
        maxLength={2000}
        value={draft.comment}
        aria-describedby={helpId}
        onChange={(e) => onDraft({ ...draft, comment: e.target.value })}
      />
      <p id={helpId} className="ugc-muted">
        La personne le lit dans son espace et dans l'e-mail de la décision, avec les points ci-dessous.
      </p>
      <label htmlFor={highlightsId}>Ce qui marche (un point par ligne, facultatif)</label>
      <textarea
        id={highlightsId}
        className="ugc-textarea"
        rows={2}
        value={draft.highlights}
        onChange={(e) => onDraft({ ...draft, highlights: e.target.value })}
      />
      <label htmlFor={fixesId}>À retoucher (un point par ligne, facultatif)</label>
      <textarea
        id={fixesId}
        className="ugc-textarea"
        rows={2}
        value={draft.fixes}
        onChange={(e) => onDraft({ ...draft, fixes: e.target.value })}
      />
      {confirming ? (
        <>
          <p className="ugc-confirm">
            La vidéo sera refusée et la personne prévenue par e-mail. Ce refus est définitif. Ton commentaire (facultatif)
            part avec lui.
          </p>
          <div className="ugc-actions">
            <button
              type="button"
              className="ugc-btn ugc-btn--strong"
              onClick={() => {
                setConfirming(false)
                onDecide('rejected')
              }}
              disabled={busy}
            >
              Confirmer le refus
            </button>
            <button type="button" className="ugc-btn" onClick={() => setConfirming(false)} disabled={busy}>
              Annuler
            </button>
          </div>
        </>
      ) : (
        <div className="ugc-actions">
          <button type="button" className="ugc-btn ugc-btn--go" onClick={() => onDecide('approved')} disabled={busy}>
            Valider
          </button>
          <button
            type="button"
            className="ugc-btn ugc-btn--strong"
            onClick={() => onDecide('changes_requested')}
            disabled={busy || empty}
          >
            Demander une correction
          </button>
          <button type="button" className="ugc-btn" onClick={() => setConfirming(true)} disabled={busy}>
            Refuser
          </button>
        </div>
      )}
      {busy && <p className="ugc-muted">Envoi de la décision…</p>}
    </div>
  )
}

// ── Détail ───────────────────────────────────────────────────────────────────

type Outcome = { ok: boolean; text: string }

function VideoDetail({
  id,
  focusRequest,
  markRead,
  now,
  onPin,
}: {
  id: string
  /** Change à chaque choix de la vidéo dans la liste. */
  focusRequest: number
  /** Vidéo choisie (clic, lien d'une alerte) : son fil est marqué lu. */
  markRead: boolean
  now: Date
  onPin: () => void
}) {
  const detail = useUgcVideo(id)
  const review = useReviewVideo()
  const queryClient = useQueryClient()
  // Commentaire, points et résultat vivent ici : ils restent après la relecture de la vidéo.
  const [reviewDraft, setReviewDraft] = useState<ReviewDraft>(EMPTY_REVIEW)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const titleId = useId()
  const loadedId = detail.data?.video.id

  // Après un choix, le focus passe au titre du détail. Sur téléphone, le détail est sous la file :
  // la page défile jusqu'à lui (le focus seul ne défile pas quand le titre dépasse à peine).
  useEffect(() => {
    const heading = headingRef.current
    if (focusRequest === 0 || !loadedId || !heading) return
    if (window.matchMedia?.('(max-width: 760px)').matches) {
      heading.focus({ preventScroll: true })
      heading.scrollIntoView?.({ block: 'start' })
    } else {
      heading.focus()
    }
  }, [focusRequest, loadedId])

  /** `shownVersion` : version affichée à relire (`null` : aucune), contrôlée par le serveur. */
  function decide(video: UgcVideo, stage: ReviewStage, decision: ReviewDecision, shown: number | null) {
    // La vidéo reste affichée après la décision, même si elle quitte la file.
    onPin()
    setOutcome(null)
    const trimmed = reviewDraft.comment.trim()
    review.mutate(
      {
        id: video.id,
        stage,
        decision,
        comment: trimmed === '' ? null : trimmed,
        highlights: splitPoints(reviewDraft.highlights),
        fixes: splitPoints(reviewDraft.fixes),
        // Le texte lu et la version vue : la décision ne passe que s'ils n'ont pas changé depuis
        // (409 « idea_changed » ou « draft_changed »).
        seen: { title: video.title, idea: video.idea, hook: video.hook, draftVersion: shown },
      },
      {
        onSuccess: (result) => {
          setReviewDraft(EMPTY_REVIEW)
          setOutcome({ ok: true, text: decidedText(stage, decision, video.title, result.video.status) })
        },
        onError: (error) => {
          const { text, reread } = reviewErrorText(error)
          setOutcome({ ok: false, text })
          if (reread) void queryClient.invalidateQueries({ queryKey: ugcKeys.all })
        },
      },
    )
  }

  const messages = outcome && (
    <p role={outcome.ok ? 'status' : 'alert'} className={outcome.ok ? 'ugc-notice' : 'ugc-error'}>
      {outcome.text}
    </p>
  )

  if (!detail.data) {
    return (
      <>
        {messages}
        {detail.isError ? (
          <p className="ugc-error">{detailErrorText(detail.error)}</p>
        ) : (
          <p className="ugc-muted">Chargement…</p>
        )}
      </>
    )
  }

  const { video, memberName, drafts } = detail.data
  const stage = pendingStage(video.status)
  const shown = stage ? shownVersion(stage, drafts) : null
  const latestFirst = [...drafts].sort((a, b) => b.version - a.version)

  return (
    <article className="ugc-card ugc-video" aria-labelledby={titleId}>
      <header className="ugc-card-head">
        <h2 id={titleId} ref={headingRef} tabIndex={-1} className="ugc-card-title ugc-video-title">
          {memberName} · {video.title}
        </h2>
        <span className="ugc-muted">{STATUS_LABELS[video.status]}</span>
      </header>
      {detail.isError && <p className="ugc-error">{detailErrorText(detail.error)}</p>}
      <div className={latestFirst.length > 0 ? 'ugc-video-body' : 'ugc-video-body ugc-video-body--text'}>
        {latestFirst.length > 0 && (
          <section className="ugc-versions" aria-label="Versions">
            {latestFirst.map((draft) => (
              <DraftVersion key={draft.id} draft={draft} now={now} />
            ))}
          </section>
        )}
        <div className="ugc-video-text">
          {latestFirst.length === 0 && (
            <p className="ugc-muted">
              {video.status === 'idea_submitted'
                ? "Pas encore de brouillon : c'est l'idée qui attend ta réponse."
                : 'Pas encore de brouillon.'}
            </p>
          )}
          {stage === 'idea' && shown !== null && (
            <p className="ugc-notice">
              {`Version jointe avant la validation de l'idée : si tu valides l'idée, la vidéo passe directement à la relecture de la version ${shown}.`}
            </p>
          )}
          {video.ideaEditedAt && video.status === 'idea_submitted' && (
            <p className="ugc-notice">{`Idée ${editedLabel(video) ?? ''}.`}</p>
          )}
          <dl className="ugc-facts ugc-facts--wide ugc-idea">
            {ideaParts(video).map((part) => (
              <div key={part.key}>
                <dt>{part.label}</dt>
                <dd className="ugc-prose">{part.text}</dd>
              </div>
            ))}
          </dl>
          <VideoThread videoId={video.id} markRead={markRead} readRequest={focusRequest} />
          {stage ? (
            <DecisionForm
              draft={reviewDraft}
              onDraft={setReviewDraft}
              busy={review.isPending}
              onDecide={(decision) =>
                decide(video, stage, decision, shown)
              }
            />
          ) : (
            <p className="ugc-muted">{`${STATUS_LABELS[video.status]} : aucune décision à prendre.`}</p>
          )}
          {messages}
        </div>
      </div>
    </article>
  )
}

// ── Filtres de « Toutes les vidéos » ─────────────────────────────────────────

function AllFilters({ filters, onChange }: { filters: UgcVideosFilters; onChange: (f: UgcVideosFilters) => void }) {
  const members = useUgcMembers()
  const memberId = useId()
  const statusId = useId()
  const sorted = [...(members.data ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'))
  return (
    <div className="ugc-selects">
      <div className="ugc-select-field">
        <label htmlFor={memberId}>Personne</label>
        <select
          id={memberId}
          className="ugc-select"
          value={filters.memberId ?? ''}
          onChange={(e) => {
            const { memberId: _drop, ...rest } = filters
            onChange(e.target.value === '' ? rest : { ...rest, memberId: e.target.value })
          }}
        >
          <option value="">Toutes les personnes</option>
          {sorted.map((m) => (
            <option key={m.id} value={m.id}>
              {m.displayName}
            </option>
          ))}
        </select>
        {members.isError && <span className="ugc-error">Liste des personnes indisponible.</span>}
      </div>
      <div className="ugc-select-field">
        <label htmlFor={statusId}>État</label>
        <select
          id={statusId}
          className="ugc-select"
          value={filters.status ?? ''}
          onChange={(e) => {
            const { status: _drop, ...rest } = filters
            const value = STATUSES.find((s) => s === e.target.value)
            onChange(value ? { ...rest, status: value } : rest)
          }}
        >
          <option value="">Tous les états</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

// ── Onglet ───────────────────────────────────────────────────────────────────

export function VideosTab() {
  // Lien d'une alerte de message : la vidéo s'ouvre dans « Toutes les vidéos », où qu'elle en soit.
  const [searchParams] = useSearchParams()
  const linkedId = searchParams.get('video')
  const [view, setView] = useState<UgcVideosView>(() => (linkedId ? 'all' : 'queue'))
  const [filters, setFilters] = useState<UgcVideosFilters>({})
  const [selectedId, setSelectedId] = useState<string | null>(() => linkedId)
  // Vidéo choisie (clic dans la liste ou lien d'une alerte) : la seule dont le fil est marqué lu.
  // Une décision garde la vidéo affichée (`onPin`) sans la choisir.
  const [chosenId, setChosenId] = useState<string | null>(() => linkedId)
  const [focusRequest, setFocusRequest] = useState(0)
  const now = useNow()
  const videos = useUgcVideos(view, view === 'all' ? filters : {})
  const items = videos.data ?? []
  // Sans choix, la première de la liste (la plus ancienne de la file) est ouverte.
  const currentId = selectedId ?? items[0]?.video.id ?? null
  const listLabel = view === 'queue' ? 'Vidéos à relire' : 'Toutes les vidéos'
  const filtered = filters.memberId !== undefined || filters.status !== undefined

  function select(id: string) {
    setSelectedId(id)
    setChosenId(id)
    setFocusRequest((n) => n + 1)
  }

  return (
    <section className="ugc-tab-panel" aria-label="Vidéos">
      <div className="ugc-filters" role="group" aria-label="Choisir la liste">
        <button type="button" className="ugc-filter" aria-pressed={view === 'queue'} onClick={() => setView('queue')}>
          À relire
        </button>
        <button type="button" className="ugc-filter" aria-pressed={view === 'all'} onClick={() => setView('all')}>
          Toutes les vidéos
        </button>
      </div>
      {view === 'all' && <AllFilters filters={filters} onChange={setFilters} />}

      <div className="ugc-videos">
        <div className="ugc-queue-col">
          {videos.isPending && <p className="ugc-muted">Chargement…</p>}
          {videos.isError && (
            <p className="ugc-error">
              {view === 'queue' ? "La file n'a pas pu être chargée" : "Les vidéos n'ont pas pu être chargées"} :{' '}
              {videos.error.message}
            </p>
          )}
          {videos.isSuccess && items.length === 0 && (
            <p className="ugc-empty">
              {view === 'queue' ? 'Rien à relire pour le moment.' : filtered ? 'Aucune vidéo pour ces filtres.' : 'Aucune vidéo.'}
            </p>
          )}
          {items.length > 0 && (
            <ul className="ugc-queue" aria-label={listLabel}>
              {items.map((item) => (
                <li key={item.video.id}>
                  <button
                    type="button"
                    className={view === 'queue' && thumbOf(item) ? 'ugc-queue-item has-thumb' : 'ugc-queue-item'}
                    aria-current={item.video.id === currentId ? 'true' : undefined}
                    onClick={() => select(item.video.id)}
                  >
                    {/* Aperçu seulement dans la file « À relire » : « Toutes les vidéos » ne charge rien par ligne. */}
                    {view === 'queue' && thumbOf(item) ? (
                      <video
                        className="ugc-queue-thumb"
                        src={previewUrl(thumbOf(item) ?? '')}
                        preload="metadata"
                        muted
                        playsInline
                        tabIndex={-1}
                        aria-hidden="true"
                      />
                    ) : null}
                    <span className="ugc-queue-type">{itemType(item, view)}</span>
                    <span className="ugc-queue-title">
                      {item.memberName} · {item.video.title}
                    </span>
                    <span className="ugc-queue-when">{itemWhen(item, now)}</span>
                    {editedLabel(item.video) && <span className="ugc-queue-when">{editedLabel(item.video)}</span>}
                    {isLate(item, now) && <span className="ugc-badge ugc-badge--warn">En retard</span>}
                    {item.unread > 0 && <span className="ugc-badge ugc-badge--warn">{unreadLabel(item.unread)}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="ugc-video-col">
          {currentId && (
            <VideoDetail
              key={currentId}
              id={currentId}
              focusRequest={focusRequest}
              markRead={chosenId === currentId}
              now={now}
              onPin={() => setSelectedId(currentId)}
            />
          )}
        </div>
      </div>
    </section>
  )
}
